import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError, api, readToken, writeToken } from '../api/client';
import type { Profile } from '../api/types';

interface AuthState {
  profile: Profile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
  /** `true` si l'utilisateur peut administrer au moins une équipe. */
  isManager: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!readToken()) {
      setProfile(null);
      setLoading(false);
      return;
    }
    try {
      setProfile(await api.profile());
    } catch (error) {
      // Jeton expiré ou révoqué : on repart proprement de l'écran de connexion
      // plutôt que de laisser l'interface à moitié chargée.
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        writeToken(null);
      }
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const { access_token } = await api.login(email, password);
      writeToken(access_token);
      setProfile(await api.profile());
    },
    [],
  );

  const signOut = useCallback(() => {
    writeToken(null);
    setProfile(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      profile,
      loading,
      signIn,
      signOut,
      isManager: profile?.user_role === 'MANAGER' || profile?.user_role === 'ADMIN',
      isAdmin: profile?.user_role === 'ADMIN',
    }),
    [profile, loading, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (value === null) throw new Error('useAuth doit être utilisé dans AuthProvider');
  return value;
}
